import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { useProposals, useDeleteProposal, useSubmitProposal, useApproveProposal, useRejectProposal, useSendProposal, useDuplicateProposal, type Proposal, type ProposalStatus } from '@/hooks/useProposals'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { formatCurrency, formatDate } from '@/lib/utils'
import {
  FileText, Search, ChevronLeft, ChevronRight, Eye, Trash2, Send, CheckCircle, XCircle, Copy,
  DollarSign, Clock, FileCheck, AlertTriangle, X, Loader2, Link2, Pencil
} from 'lucide-react'
import { CreateProposalWizard } from '@/components/CreateProposalWizard'

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  pending_approval: { label: 'Aguardando Aprovação', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
  approved: { label: 'Aprovada', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  rejected: { label: 'Rejeitada', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  sent: { label: 'Enviada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  accepted: { label: 'Aceita', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' },
  declined: { label: 'Recusada', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  expired: { label: 'Expirada', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
}

function ApprovalBadge({ level }: { level: number }) {
  const labels: Record<number, string> = { 1: 'Rep', 2: 'Gestor', 3: 'CFO', 4: 'CEO' }
  return <span className="text-xs text-muted-foreground">Nível {level} ({labels[level] || `N${level}`})</span>
}

export default function ProposalsPage() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null)
  const [editingProposal, setEditingProposal] = useState<Proposal | null>(null)
  const [rejectComment, setRejectComment] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [confirmDeleteProposal, setConfirmDeleteProposal] = useState<Proposal | null>(null)

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
    const timer = setTimeout(() => { setSearch(value); setPage(1) }, 400)
    return () => clearTimeout(timer)
  }, [])

  const { data, isLoading } = useProposals({
    page: 1,
    perPage: 500,
  })

  const deleteProposal = useDeleteProposal()
  const submitProposal = useSubmitProposal()
  const approveProposal = useApproveProposal()
  const rejectProposal = useRejectProposal()
  const sendProposal = useSendProposal()
  const duplicateProposal = useDuplicateProposal()

  const allProposals = data?.items || []
  const proposals = useMemo(() => {
    let items = allProposals
    if (search) { const s = search.toLowerCase(); items = items.filter(p => (p.opportunity_name || '').toLowerCase().includes(s) || (p.lead_company_name || '').toLowerCase().includes(s)) }
    if (statusFilter) items = items.filter(p => p.status === statusFilter)
    return items
  }, [allProposals, search, statusFilter])
  

  const stats = {
    total: data?.total || 0,
    pending: proposals.filter(p => p.status === 'pending_approval').length,
    value: proposals.reduce((s, p) => s + p.final_price, 0),
    approved: proposals.filter(p => ['approved', 'sent', 'accepted'].includes(p.status)).length,
  }

  function handleReject(proposalId: string) {
    rejectProposal.mutate({ proposalId, comments: rejectComment }, {
      onSuccess: () => { setShowRejectModal(false); setRejectComment(''); setSelectedProposal(null) }
    })
  }

  function handleStatusFilter(value: string) {
    setStatusFilter(value)
    setPage(1)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Propostas</h1>
          <p className="text-muted-foreground text-sm">Gerencie propostas comerciais e aprovações</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900"><FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" /></div>
          <div><p className="text-sm text-muted-foreground">Total</p><p className="text-xl font-bold">{stats.total}</p></div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900"><Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" /></div>
          <div><p className="text-sm text-muted-foreground">Aguardando</p><p className="text-xl font-bold">{stats.pending}</p></div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900"><FileCheck className="h-5 w-5 text-green-600 dark:text-green-400" /></div>
          <div><p className="text-sm text-muted-foreground">Aprovadas</p><p className="text-xl font-bold">{stats.approved}</p></div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900"><DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /></div>
          <div><p className="text-sm text-muted-foreground">Valor Total</p><p className="text-xl font-bold">{formatCurrency(stats.value)}</p></div>
        </Card>
      </div>

      {/* Filters — both search and status filter now send params to API */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar propostas..." value={searchInput}
            onChange={e => handleSearchChange(e.target.value)} />
        </div>
        <select className="px-3 py-2 rounded-md border bg-background text-sm" value={statusFilter}
          onChange={e => handleStatusFilter(e.target.value)}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <DataGrid
          gridId="proposals"
          columns={[
            { id: 'name', header: 'Proposta', accessor: (p: Proposal) => <div><div className="font-medium">{p.opportunity_name || 'Sem oportunidade'}</div><div className="text-xs text-muted-foreground">v{p.version} &bull; {p.creator_name || 'N/A'}</div></div>, sortValue: (p) => p.opportunity_name || '', filterValue: (p) => (p.opportunity_name || '') + ' ' + (p.creator_name || '') },
            { id: 'client', header: 'Cliente', accessor: (p: Proposal) => <span className="text-muted-foreground">{p.lead_company_name || '—'}</span>, sortValue: (p) => p.lead_company_name || '', filterValue: (p) => p.lead_company_name || '' },
            { id: 'value', header: 'Valor', accessor: (p: Proposal) => <div><div className="font-medium">{formatCurrency(p.final_price)}</div>{p.recurring_monthly_total > 0 && <div className="text-xs text-muted-foreground">{formatCurrency(p.recurring_monthly_total)}/mês</div>}</div>, sortValue: (p) => p.final_price, filterValue: (p) => formatCurrency(p.final_price) },
            { id: 'discount', header: 'Desconto', accessor: (p: Proposal) => p.discount_percent > 0 ? <span className="text-amber-600">{p.discount_percent.toFixed(1)}%</span> : <span>—</span>, sortValue: (p) => p.discount_percent, filterValue: (p) => p.discount_percent > 0 ? p.discount_percent.toFixed(1) + '%' : '' },
            { id: 'status', header: 'Status', accessor: (p: Proposal) => <StatusBadge status={p.status} />, sortValue: (p) => p.status, filterValue: (p) => STATUS_CONFIG[p.status]?.label || p.status },
            { id: 'approval', header: 'Aprovação', accessor: (p: Proposal) => <ApprovalBadge level={p.current_approval_level} />, sortValue: (p) => p.current_approval_level, filterValue: (p) => 'Nível ' + p.current_approval_level },
            { id: 'valid_until', header: 'Validade', accessor: (p: Proposal) => <span className="text-muted-foreground">{formatDate(p.valid_until)}</span>, sortValue: (p) => new Date(p.valid_until).getTime(), filterValue: (p) => formatDate(p.valid_until) },
            { id: 'actions', header: 'Ações', sortable: false, filterable: false, align: 'right' as const, accessor: (p: Proposal) => <div className="flex items-center justify-end gap-1" onClick={(e: any) => e.stopPropagation()}><button className="p-1.5 rounded hover:bg-muted" title="Ver" onClick={() => setSelectedProposal(p)}><Eye className="h-4 w-4" /></button><button className="p-1.5 rounded hover:bg-muted" title="Editar" onClick={() => setEditingProposal(p)}><Pencil className="h-4 w-4" /></button><button className="p-1.5 rounded hover:bg-muted" title="Compartilhar" onClick={() => navigate(`/proposals/${p.id}/share`)}><Link2 className="h-4 w-4" /></button><button className="p-1.5 rounded hover:bg-muted" title="Duplicar" onClick={() => duplicateProposal.mutate(p.id)}><Copy className="h-4 w-4" /></button><button className="p-1.5 rounded hover:bg-muted text-red-500" title="Excluir" onClick={() => setConfirmDeleteProposal(p)}><Trash2 className="h-4 w-4" /></button></div> },
          ] as DataGridColumn<Proposal>[]}
          data={proposals}
          isLoading={isLoading}
          onRowClick={(p) => setSelectedProposal(p)}
          emptyMessage="Nenhuma proposta encontrada"
          pageSize={20}
        />
      </Card>

      {/* Detail Modal */}
      {selectedProposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedProposal(null)}>
          <div className="bg-background rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-bold">{selectedProposal.opportunity_name || 'Proposta'}</h2>
                <p className="text-sm text-muted-foreground">v{selectedProposal.version} • {selectedProposal.lead_company_name || 'N/A'}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={selectedProposal.status} />
                <button onClick={() => setSelectedProposal(null)} className="p-1 rounded hover:bg-muted"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="p-4 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-xs text-muted-foreground">Preço Lista</p><p className="font-medium">{formatCurrency(selectedProposal.list_price)}</p></div>
                <div><p className="text-xs text-muted-foreground">Desconto</p><p className="font-medium">{selectedProposal.discount_percent.toFixed(1)}%</p></div>
                <div><p className="text-xs text-muted-foreground">Preço Final</p><p className="text-lg font-bold text-green-600">{formatCurrency(selectedProposal.final_price)}</p></div>
                <div><p className="text-xs text-muted-foreground">Recorrente</p><p className="font-medium">{formatCurrency(selectedProposal.recurring_monthly_total)}/mês</p></div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Itens ({selectedProposal.proposal_items.length})</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left p-2">Item</th><th className="text-center p-2">Qtd</th>
                      <th className="text-right p-2">Unit.</th><th className="text-center p-2">Desc.</th>
                      <th className="text-right p-2">Subtotal</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {selectedProposal.proposal_items.map(item => (
                        <tr key={item.id}>
                          <td className="p-2"><div className="font-medium">{item.item_name}</div><div className="text-xs text-muted-foreground">{item.billing_type}{item.billing_cycle ? ` (${item.billing_cycle})` : ''}</div></td>
                          <td className="p-2 text-center">{item.quantity}</td>
                          <td className="p-2 text-right">{formatCurrency(item.unit_price)}</td>
                          <td className="p-2 text-center">{item.discount_percent > 0 ? `${item.discount_percent}%` : '—'}</td>
                          <td className="p-2 text-right font-medium">{formatCurrency(item.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {selectedProposal.approvals.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Histórico de Aprovações</h3>
                  <div className="space-y-2">
                    {selectedProposal.approvals.map(a => (
                      <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg border">
                        {a.status === 'approved' ? <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" /> :
                         a.status === 'rejected' ? <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" /> :
                         <Clock className="h-5 w-5 text-amber-500 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">Nível {a.level} — {a.approver_role}</div>
                          {a.approver_name && <div className="text-xs text-muted-foreground">{a.approver_name}</div>}
                          {a.comments && <div className="text-xs mt-1 italic">"{a.comments}"</div>}
                        </div>
                        <div className="text-xs text-muted-foreground flex-shrink-0">{a.decided_at ? formatDate(a.decided_at) : 'Pendente'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedProposal.notes && (
                <div><h3 className="font-semibold mb-1">Observações</h3><p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedProposal.notes}</p></div>
              )}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {selectedProposal.status === 'draft' && (
                  <Button size="sm" onClick={() => submitProposal.mutate(selectedProposal.id)} disabled={submitProposal.isPending}>
                    <Send className="h-4 w-4 mr-1" /> Enviar para Aprovação
                  </Button>
                )}
                {selectedProposal.status === 'pending_approval' && (
                  <>
                    <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => approveProposal.mutate({ proposalId: selectedProposal.id })} disabled={approveProposal.isPending}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Aprovar
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => setShowRejectModal(true)}>
                      <XCircle className="h-4 w-4 mr-1" /> Rejeitar
                    </Button>
                  </>
                )}
                {selectedProposal.status === 'approved' && (
                  <Button size="sm" onClick={() => sendProposal.mutate(selectedProposal.id)} disabled={sendProposal.isPending}>
                    <Send className="h-4 w-4 mr-1" /> Enviar ao Cliente
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => duplicateProposal.mutate(selectedProposal.id)}>
                  <Copy className="h-4 w-4 mr-1" /> Duplicar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && selectedProposal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-bold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" /> Rejeitar Proposta</h3>
            <p className="text-sm text-muted-foreground mt-1">Informe o motivo da rejeição:</p>
            <textarea className="w-full mt-3 p-3 border rounded-lg bg-background text-sm min-h-[100px]" placeholder="Motivo da rejeição..."
              value={rejectComment} onChange={e => setRejectComment(e.target.value)} />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowRejectModal(false)}>Cancelar</Button>
              <Button variant="destructive" size="sm" onClick={() => handleReject(selectedProposal.id)} disabled={rejectProposal.isPending}>
                {rejectProposal.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />} Rejeitar
              </Button>
            </div>
          </div>
        </div>
      )}

      {editingProposal && (
        <CreateProposalWizard
          opportunityId={editingProposal.opportunity_id}
          mode="edit"
          proposal={editingProposal}
          onClose={() => setEditingProposal(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirmDeleteProposal}
        title="Excluir proposta?"
        message={`A proposta "${confirmDeleteProposal?.title || ''}" será removida. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteProposal.isPending}
        onConfirm={async () => {
          if (!confirmDeleteProposal) return
          try {
            await deleteProposal.mutateAsync(confirmDeleteProposal.id)
            setConfirmDeleteProposal(null)
          } catch (e: any) {
            alert('Erro ao excluir: ' + (e?.response?.data?.detail || e?.message || ''))
          }
        }}
        onCancel={() => setConfirmDeleteProposal(null)}
      />
    </div>
  )
}
